/**
 * `KEY-LIVE`. OWNER: web-frontend-engineer.
 *
 * EXACTLY ONE polite live region per page and EXACTLY ONE assertive region.
 * Both live in `AppShell`; everything else announces through this context.
 *
 * The assertive region is used only for a refused submission or a lost
 * connection. Nothing else is ever assertive — not a price change, not a
 * withdrawn option, not a saved draft.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

interface AnnouncerValue {
  readonly polite: string;
  readonly assertive: string;
  readonly say: (message: string) => void;
  readonly refuse: (message: string) => void;
}

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const lastPolite = useRef('');

  // Announced ONCE per settled value — never per keystroke, never twice for the
  // same sentence (COMPONENT_SPEC §5.1, FLOWS_SPEC §6.3.4).
  const say = useCallback((message: string) => {
    if (message === lastPolite.current) return;
    lastPolite.current = message;
    setPolite(message);
  }, []);

  const refuse = useCallback((message: string) => setAssertive(message), []);

  const value = useMemo(
    () => ({ polite, assertive, say, refuse }),
    [polite, assertive, say, refuse],
  );
  return <AnnouncerContext.Provider value={value}>{children}</AnnouncerContext.Provider>;
}

export function useAnnouncer(): AnnouncerValue {
  const value = useContext(AnnouncerContext);
  if (value === null) {
    return { polite: '', assertive: '', say: () => {}, refuse: () => {} };
  }
  return value;
}
